// REACT IMPORTS
import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

// COMPONENT IMPORTS
import InputField from "components/form/InputField";
import Button from "components/form/Button";
import MobileInputNew from "components/form/MobileInputNew";
import { useAppDispatch } from "store/hooks";
import { accessRequest } from "store/slices/authSlice";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import { decode } from "utils/HelperFunctions";
import { useTid } from "utils/testid";
import { activateOnKey } from "utils/activateOnKey";

/* ------------------------------------- LOGIN VIA CRED FORM ------------------------------------- */

const RequestAccessForm = () => {
  const tid = useTid();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const initialVal = {
    name: "",
    email: "",
    phone_number: "",
  };

  const [values, setValues] = useState<any>(initialVal);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const validateInput = () => {
    if (values.name.trim() === "" || !/^[a-zA-Z\s]*$/.test(values.name)) {
      setErrMsg("Name cannot have any special character");
      return false;
    }
    if (values.phone_number.trim() === "") {
      setErrMsg("Enter valid phone number");
      return false;
    }
    if (
      values.email.trim() === "" ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)
    ) {
      setErrMsg("Enter valid email");
      return false;
    }
    return true;
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    if (!validateInput()) {
      return;
    }
    setErrMsg(null);
    setIsLoading(true);
    try {
      let payload = { ...values, cluster_id: clusterId };
      let res: any = await dispatch(accessRequest(payload)).unwrap();
      if (res && res?.body?.success) {
        navigate(`/${cluster_id}/access-request-send-successfully`);
      }
    } catch (error: any) {
      console.log("request send err: -->", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const checkIsDisabled = () => {
    return !Object.values(values).every((value) => value !== "");
  };

  return (
    <div
      className="animate__animated animate__fadeIn w-full lg:max-w-[520px]"
      data-testid={tid("form-shell")}
    >
      <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
        Request Access
      </div>
      <p className="lg:py-10 pt-6 pb-10 max-w-[520px] text-txtParagraph lg:text-base text-sm leading-normal font-normal">
        Please complete the form below to request access
      </p>
      <form onSubmit={onSubmit} data-testid={tid("request-access-form")}>
        <div className="max-w-[520px] flex flex-col gap-10 lg:gap-[60px]">
          <div className="flex flex-col lg:gap-6 gap-5">
            <InputField
              name="name"
              label="Name"
              data-testid={tid("name-input")}
              aria-label="Full name"
              placeholder="Enter name"
              value={values?.name}
              onChange={(e: any) => {
                setValues({
                  ...values,
                  name: e?.target?.value,
                });
              }}
              _className="!font-medium"
              onKeyDown={(e: any) => {
                if (e.key >= "0" && e.key <= "9") {
                  e.preventDefault();
                }
              }}
            />
            <div>
              <label
                className={`lg:text-base text-xs text-txtSecondary font-medium leading-normal uppercase`}
              >
                Phone number
              </label>
              <MobileInputNew
                inputValue={values?.phone_number}
                userCountry="US"
                data-testid={tid("phone-number-input")}
                aria-label="Phone number"
                setCountry={(country: string) => {
                  // Country change handled here if needed
                }}
                phoneClassName={"!lg:py-[15px] !py-3 !px-[15px]"}
                setInputValue={(val: string) => {
                  setValues({
                    ...values,
                    phone_number: val,
                  });
                }}
                _className="!font-medium"
              />
            </div>
            <InputField
              type="email"
              name="email"
              label="Email"
              data-testid={tid("email-input")}
              aria-label="Email address"
              placeholder="Enter your email address"
              value={values?.email}
              onChange={(e: any) => {
                setValues({
                  ...values,
                  email: e?.target?.value,
                });
              }}
              _className="!font-medium"
            />
          </div>
          <div className="w-full grid place-items-center">
            <Button
              className="w-full"
              text="Request Access"
              type="submit"
              data-testid={tid("request-access-submit-button")}
              aria-label="Submit access request"
              isLoading={isLoading}
              isDisabled={checkIsDisabled()}
              variant="variable"
            />
            {errMsg && (
              <div
                className="flex items-center gap-[5px] mt-3 px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
                role="alert"
                data-testid={tid("validation-error-alert")}
              >
                <InfoRedCircleIcon aria-hidden="true" />
                <div className="lg:text-base text-sm text-red leading-[20.83px]">
                  {errMsg}
                </div>
              </div>
            )}
            <div className="pt-[12px] lg:text-base text-sm w-full grid place-items-center">
              <span
                className="text-blue hover:!text-btnPrimaryHover lg:py-[10px] py-[2px] px-4 cursor-pointer"
                onClick={() => navigate(-1)}
                onKeyDown={activateOnKey(() => navigate(-1))}
                role="button"
                tabIndex={0}
                data-testid={tid("go-back-button")}
                aria-label="Go back"
              >
                Go back
              </span>
            </div>
            <div className="w-full h-[1px] bg-strokePrimary lg:mt-4 mt-3"></div>
          </div>
        </div>
      </form>
      <div className="max-w-[520px] flex flex-col gap-4 items-center pt-4">
        <div className="flex gap-[6px] items-center justify-start w-full">
          <p className=" text-sm lg:text-base text-txtParagraph leading-normal">
            Already have an account?
          </p>
          <Link
            to={`/${cluster_id}/login`}
            className="cursor-pointer text-sm lg:text-base text-blue hover:!text-btnPrimaryHover font-medium leading-normal"
            data-testid={tid("login-link")}
            aria-label="Log in instead"
          >
            Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RequestAccessForm;
